import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Plus, Calendar, X, SlidersHorizontal, Trash2 } from 'lucide-react';
import { EvaluationDeleteDialog } from '@/components/admin/AdminDeleteDialogs';
import { SyncedHScroll } from '@/components/shared/SyncedHScroll';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { suiteDisplayName, localizeAssignmentTitle } from '@/lib/i18nTitles';
import { LocalizedDateInput } from '@/components/shared/LocalizedDateInput';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NewEvaluationDialog } from '@/components/admin/NewEvaluationDialog';
import ConfigureAssignmentDialog from '@/components/admin/ConfigureAssignmentDialog';
import { adminApi } from '@/lib/adminApi';
import { ListGridToggle } from '@/components/shared/ListGridToggle';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { useSort } from '@/lib/useSort';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { assignmentStateReason } from '@/lib/assignmentState';
import { formatApiError } from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const ACTIVE_STATUSES = ['in_progress', 'requested', 'under_review'];

const initials = (name) => (name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

// BUG-platform — Sortable columns via shared `useSort()` hook.
const SORT_KEYS = {
  evaluatee: (e) => (e.candidate?.name || '').toLowerCase(),
  suite:     (e) => (e.suite_code || '').toLowerCase(),
  company:   (e) => (e.company?.name || '').toLowerCase(),
  position:  (e) => (e.position || '').toLowerCase(),
  progress:  (e) => e.progress || 0,
  deadline:  (e) => e.deadline ? new Date(e.deadline).getTime() : 0,
  status:    (e) => (e.status || ''),
};

export default function AdminEvaluations() {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isAdvisor = user?.role === 'hm_advisor';
  const isHmAdmin = user?.role === 'hm_admin';
  const [deleteEval, setDeleteEval] = useState(null);
  const [q, setQ] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('status') === 'active' ? 'active' : (searchParams.get('status') || 'active');
  const [tab, setTab] = useState(initialTab);
  // Iter 41.10 — RBAC hardening: hm_advisor is ALWAYS book-scoped. The
  // "View all" toggle has been removed. The backend forcibly ignores
  // ?book=all for advisors.
  const showAll = false;
  const [evalOpen, setEvalOpen] = useState(false);
  const [configTarget, setConfigTarget] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [companies, setCompanies] = useState({});
  const [users, setUsers] = useState({});
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const sort = useSort();
  const navigate = useNavigate();

  // FIX 4 (Iter 41.32) — admin filters
  const [filters, setFilters] = useState({
    company_id: searchParams.get('company_id') || '',
    suite_code: searchParams.get('suite') || '',
    status: '', assigned_by: '',
    date_from: '', date_to: '',
  });
  const clearFilters = () => setFilters({
    company_id: '', suite_code: '', status: '', assigned_by: '',
    date_from: '', date_to: '',
  });
  // Post-D16 A2.B — filters are collapsible so the record list keeps most of
  // the viewport. Open by default only when the URL pre-applies a filter.
  const [filtersOpen, setFiltersOpen] = useState(
    () => Boolean(searchParams.get('company_id') || searchParams.get('suite'))
  );
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const reload = async () => {
    setLoading(true);
    try {
      // Iter 40 — backend scopes assignments to the advisor's client book.
      // Admins/reviewers always see everything; the toggle only matters for
      // hm_advisor.
      const opts = {
        ...(isAdvisor && showAll ? { book: 'all' } : {}),
        ...(filters.company_id  ? { company_id:  filters.company_id  } : {}),
        ...(filters.suite_code  ? { suite_code:  filters.suite_code  } : {}),
        ...(filters.status      ? { status:      filters.status      } : {}),
        ...(filters.assigned_by ? { assigned_by: filters.assigned_by } : {}),
        ...(filters.date_from   ? { date_from:   filters.date_from   } : {}),
        ...(filters.date_to     ? { date_to:     filters.date_to     } : {}),
        ...(tab === 'archived'  ? { archived: 'true' } : {}),
      };
      const [list, cos, us] = await Promise.all([
        adminApi.listAssignments(opts),
        adminApi.listCompanies(opts),
        adminApi.listAllUsers(),
      ]);
      setAssignments(list);
      setCompanies(Object.fromEntries(cos.map(c => [c.id, c])));
      setUsers(Object.fromEntries(us.map(u => [u.id, u])));
      setAdvisors(us.filter((u) => ['hm_advisor', 'hm_admin'].includes(u.role)));
    } catch {
      toast.error(t('pages.evaluations.loadError', { defaultValue: 'Failed to load evaluations' }));
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [isAdvisor, showAll, filters, tab === 'archived']);

  // Sync URL when tab changes
  useEffect(() => {
    const cur = searchParams.get('status') || 'active';
    if (cur !== tab) {
      const next = new URLSearchParams(searchParams);
      if (tab === 'active') next.delete('status'); else next.set('status', tab);
      setSearchParams(next, { replace: true });
    }
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // UAT 2026-09 — bulk archive of eligible completed evaluations + grouping
  // by the real batch relationship (created_from_request_id).
  const [bulkArchBusy, setBulkArchBusy] = useState(false);
  const [groupByRequest, setGroupByRequest] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmRows, setConfirmRows] = useState(null); // rows pending bulk-archive confirmation
  const ARCHIVE_ELIGIBLE = ['submitted', 'completed', 'released'];
  const isEligible = (e) => !e.archived && ARCHIVE_ELIGIBLE.includes(e.status);
  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const doBulkArchive = async (rows) => {
    const eligible = rows.filter(isEligible);
    setConfirmRows(null);
    if (!eligible.length) return;
    setBulkArchBusy(true);
    try {
      await Promise.all(eligible.map((e) => adminApi.archiveAssignment(e.id).catch(() => null)));
      toast.success(isESlang()
        ? `${eligible.length} evaluaciones archivadas`
        : `${eligible.length} evaluations archived`);
      setSelected(new Set());
      reload();
    } finally { setBulkArchBusy(false); }
  };
  const isESlang = () => (i18n.language || 'es').toLowerCase().startsWith('es');

  const decorated = useMemo(() => assignments.map(a => ({
    ...a,
    candidate: users[a.assigned_to]
      || (a.candidate_name ? { name: a.candidate_name, email: a.candidate_email } : undefined),
    company: companies[a.company_id],
  })), [assignments, users, companies]);

  const filtered = decorated.filter(e => {
    const fields = [e.candidate?.name, e.company?.name, e.position, e.title].filter(Boolean);
    const matchQ = fields.some(f => f.toLowerCase().includes(q.toLowerCase()));
    let matchTab = true;
    if (tab === 'active') matchTab = ACTIVE_STATUSES.includes(e.status);
    else if (tab === 'archived') matchTab = true; // backend already returns archived only
    else if (tab !== 'all') matchTab = e.status === tab;
    return matchQ && matchTab;
  });

  // BUG-platform — apply current sort.
  const sorted = useMemo(() => sort.apply(filtered, SORT_KEYS), [filtered, sort]);
  const groupKey = (e) => e.created_from_request_id || '';
  const displayRows = useMemo(() => {
    if (!groupByRequest) return sorted;
    return [...sorted].sort((a, b) => {
      const ga = groupKey(a); const gb = groupKey(b);
      if (ga === gb) return 0;
      if (!ga) return 1; if (!gb) return -1;
      return ga.localeCompare(gb);
    });
  }, [sorted, groupByRequest]);

  // Post-D16 A2.A — select-all across eligible visible rows.
  const eligibleRows = useMemo(() => displayRows.filter(isEligible), [displayRows]); // eslint-disable-line react-hooks/exhaustive-deps
  const allSelected = eligibleRows.length > 0 && eligibleRows.every((e) => selected.has(e.id));

  // Founder UX sprint — the workspace must explain its purpose immediately.
  const isES = (i18n.language || 'es').toLowerCase().startsWith('es');
  const [purposeDismissed, setPurposeDismissed] = useState(() => {
    try { return localStorage.getItem('evals_purpose_dismissed') === '1'; } catch { return false; }
  });
  const dismissPurpose = () => {
    try { localStorage.setItem('evals_purpose_dismissed', '1'); } catch { /* noop */ }
    setPurposeDismissed(true);
  };
  // State-aware action label: buttons describe the action being performed.
  const openLabel = (status) => {
    if (status === 'draft') return isES ? 'Revisar asignación' : 'Review assignment';
    if (status === 'requested' || status === 'in_progress') return isES ? 'Ver progreso' : 'View progress';
    return isES ? 'Ver evaluación' : 'View evaluation';
  };

  return (
    <>
      <Topbar
        title={t('pages.evaluations.title')}
        subtitle={t('pages.evaluations.subtitle')}
        actions={
          <Button
            size="sm"
            data-testid="evaluations-new-btn"
            onClick={() => setEvalOpen(true)}
            className="bg-gradient-primary hover:opacity-90 gap-1.5"
          >
            <Plus className="h-4 w-4" /> {t('pages.evaluations.newEvaluation')}
          </Button>
        }
      />
      <div className="px-6 lg:px-8 py-4 space-y-3">
        {!purposeDismissed && (
          <Card className="p-3 shadow-card border-l-4 border-l-primary flex items-start gap-3" data-testid="evals-purpose-banner">
            <div className="flex-1 text-sm text-muted-foreground leading-relaxed">
              <p className="font-semibold text-foreground mb-0.5">{isES ? '¿Para qué sirve este espacio?' : 'What is this workspace for?'}</p>
              {isES
                ? 'Este espacio permite a los consultores gestionar cada evaluación asignada, completar la configuración pendiente, monitorear el progreso de los participantes y supervisar el ciclo de vida de la evaluación hasta la generación de los informes.'
                : 'This workspace allows consultants to manage every assigned evaluation, complete pending setup, monitor participant progress, and supervise the evaluation lifecycle until reports are generated.'}
            </div>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={dismissPurpose} data-testid="evals-purpose-dismiss">
              <X className="h-4 w-4" />
            </Button>
          </Card>
        )}
        <Tabs value={tab} onValueChange={setTab}>
          <Card className="p-3 shadow-card flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-60">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="evaluations-search"
                placeholder={t('pages.evaluations.searchPh')}
                value={q}
                onChange={e => setQ(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
            <TabsList className="bg-muted">
              <TabsTrigger value="all">{t('pages.evaluations.tabAll')}</TabsTrigger>
              <TabsTrigger value="active">{t('pages.evaluations.tabActive')}</TabsTrigger>
              <TabsTrigger value="draft" data-testid="evals-tab-draft">{t('pages.evaluations.tabDraft', { defaultValue: 'Drafts' })}</TabsTrigger>
              <TabsTrigger value="requested">{t('pages.evaluations.tabRequested')}</TabsTrigger>
              <TabsTrigger value="in_progress">{t('pages.evaluations.tabInProgress')}</TabsTrigger>
              <TabsTrigger value="under_review">{t('pages.evaluations.tabUnderReview')}</TabsTrigger>
              <TabsTrigger value="completed">{t('pages.evaluations.tabCompleted')}</TabsTrigger>
              <TabsTrigger value="archived" data-testid="evals-tab-archived">{t('pages.evaluations.tabArchived', { defaultValue: isES ? 'Archivadas' : 'Archived' })}</TabsTrigger>
            </TabsList>
            <ListGridToggle value={view} onChange={setView} testIdPrefix="evals-view" />
            <Button
              size="sm" variant={filtersOpen ? 'default' : 'outline'} className="h-9 gap-1.5"
              onClick={() => setFiltersOpen((v) => !v)} data-testid="evals-filters-toggle"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {isES ? 'Filtros' : 'Filters'}
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs tabular-nums">{activeFilterCount}</Badge>
              )}
            </Button>
            <Button
              size="sm" variant={groupByRequest ? 'default' : 'outline'} className="h-9"
              onClick={() => setGroupByRequest((v) => !v)} data-testid="evals-group-toggle"
            >
              {isES ? 'Agrupar por solicitud' : 'Group by request'}
            </Button>
            {isHmAdmin && selected.size > 0 && (
              <Button
                size="sm" className="h-9" disabled={bulkArchBusy}
                onClick={() => setConfirmRows(sorted.filter((e) => selected.has(e.id)))} data-testid="evals-archive-selected"
              >
                {isES ? `Archivar seleccionadas (${selected.size})` : `Archive selected (${selected.size})`}
              </Button>
            )}
            {isHmAdmin && (tab === 'completed' || tab === 'all') && sorted.some(isEligible) && (
              <Button
                size="sm" variant="outline" className="h-9" disabled={bulkArchBusy}
                onClick={() => setConfirmRows(sorted)} data-testid="evals-bulk-archive"
              >
                {isES ? 'Archivar completadas visibles' : 'Archive visible completed'}
              </Button>
            )}
          </Card>

          {/* FIX 4 — column filters (Iter 41.32) · Post-D16 A2.B collapsible */}
          {filtersOpen && (
          <Card className="p-3 shadow-card grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-3" data-testid="evals-filters">
            <Select value={filters.company_id || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, company_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="h-9" data-testid="evals-filter-company"><SelectValue placeholder={t('pages.evaluations.filters.client', { defaultValue: 'Cliente' })} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('pages.evaluations.filters.allClients', { defaultValue: 'Todos los clientes' })}</SelectItem>
                {Object.values(companies).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.suite_code || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, suite_code: v === 'all' ? '' : v }))}>
              <SelectTrigger className="h-9" data-testid="evals-filter-suite"><SelectValue placeholder={t('pages.evaluations.filters.suite', { defaultValue: 'Suite' })} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('pages.evaluations.filters.allSuites', { defaultValue: 'Todas las suites' })}</SelectItem>
                {['talentmap','talentcheck','talentstart','talentheart','talentfix','climara','talentcompetency','talenttrain','intelligence'].map((s) => (
                  <SelectItem key={s} value={s}>{suiteDisplayName(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.status || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? '' : v }))}>
              <SelectTrigger className="h-9" data-testid="evals-filter-status"><SelectValue placeholder={t('pages.evaluations.filters.status', { defaultValue: 'Estado' })} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('pages.evaluations.filters.allStatuses', { defaultValue: 'Todos los estados' })}</SelectItem>
                {['draft','requested','in_progress','under_review','completed','released'].map((s) => (
                  <SelectItem key={s} value={s}>{t(`pages.evaluations.tab${s.replace(/(^|_)(\w)/g, (_, __, c) => c.toUpperCase())}`, { defaultValue: s })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isHmAdmin && (
              <Select value={filters.assigned_by || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, assigned_by: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-9" data-testid="evals-filter-assignee"><SelectValue placeholder={t('pages.evaluations.filters.assignee', { defaultValue: 'Asignado a' })} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pages.evaluations.filters.allAssignees', { defaultValue: 'Todos los asesores' })}</SelectItem>
                  {advisors.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* UAT-004 — the date filters act on the CREATION date */}
            <div>
              <label className="text-xs text-muted-foreground block mb-0.5" htmlFor="evals-date-from">
                {t('pages.evaluations.filters.createdFrom', { defaultValue: isES ? 'Fecha de creación — desde' : 'Creation date — from' })}
              </label>
              <LocalizedDateInput
                id="evals-date-from"
                value={filters.date_from}
                onChange={(v) => setFilters((f) => ({ ...f, date_from: v }))}
                data-testid="evals-filter-date-from"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-0.5" htmlFor="evals-date-to">
                {t('pages.evaluations.filters.createdTo', { defaultValue: isES ? 'Fecha de creación — hasta' : 'Creation date — to' })}
              </label>
              <LocalizedDateInput
                id="evals-date-to"
                value={filters.date_to}
                onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))}
                data-testid="evals-filter-date-to"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-6 flex justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={clearFilters}
                data-testid="evals-filters-clear"
                disabled={!Object.values(filters).some(Boolean)}
              >
                <X className="h-4 w-4 mr-1" />
                {t('pages.evaluations.filters.clear', { defaultValue: 'Limpiar filtros' })}
              </Button>
            </div>
          </Card>
          )}

          <TabsContent value={tab} className="mt-0">
            {view === 'grid' ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="evals-grid">
              {sorted.map((e) => (
                <Card key={e.id} className="p-5 shadow-card hover:shadow-medium transition-smooth cursor-pointer" data-testid={`eval-card-${e.id}`} onClick={() => navigate(`/admin/evaluations/${e.id}`)}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /><span className="text-xs text-muted-foreground">{e.deadline ? new Date(e.deadline).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US') : '—'}</span></div>
                    <StatusBadge status={e.status} />
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="h-10 w-10"><AvatarFallback className="bg-gradient-primary text-white text-xs font-bold">{initials(e.candidate?.name)}</AvatarFallback></Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{e.candidate?.name || '—'}
                        {e.candidate_type === 'external' && (
                          <Badge variant="outline" className="ml-1.5 text-xs align-middle border-info/40 text-info" data-testid={`evaluation-external-badge-${e.id}`}>
                            {t('pages.evaluations.externalBadge', { defaultValue: isES ? 'Externa' : 'External' })}
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{e.position || '—'}</p>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mb-2">{e.company?.name || ''}</div>
                  <Progress value={e.progress || 0} className="h-1.5" />
                  {assignmentStateReason(e, i18n.language?.startsWith('es')) && (
                    <p className="text-xs text-muted-foreground mt-1.5 leading-snug" data-testid={`eval-card-reason-${e.id}`}>
                      {assignmentStateReason(e, i18n.language?.startsWith('es'))}
                    </p>
                  )}
                </Card>
              ))}
              {!sorted.length && <p className="text-sm text-muted-foreground italic col-span-full">{t('common.noData', { defaultValue: 'No data' })}</p>}
            </div>
            ) : (
            <Card className="shadow-card">
              <SyncedHScroll testId="evals-table-scroll">
              <Table containerClassName="!overflow-visible">
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    {isHmAdmin && tab !== 'archived' && (
                      <TableHead className="w-8">
                        <span title={eligibleRows.length
                          ? (isES ? 'Seleccionar todas las elegibles' : 'Select all eligible')
                          : (isES ? 'No hay evaluaciones elegibles para archivar en esta vista' : 'No archive-eligible evaluations in this view')}>
                          <Checkbox
                            checked={allSelected}
                            disabled={!eligibleRows.length}
                            onCheckedChange={(v) => setSelected(v ? new Set(eligibleRows.map((e) => e.id)) : new Set())}
                            data-testid="evals-select-all"
                          />
                        </span>
                      </TableHead>
                    )}
                    <TableHead><SortableHeader sort={sort} k="evaluatee" testIdPrefix="evals">{t('pages.evaluations.evaluatee')}</SortableHeader></TableHead>
                    <TableHead><SortableHeader sort={sort} k="suite" testIdPrefix="evals">{t('pages.evaluations.suite')}</SortableHeader></TableHead>
                    <TableHead className="hidden lg:table-cell min-w-[170px]"><SortableHeader sort={sort} k="company" testIdPrefix="evals">{t('pages.evaluations.company')}</SortableHeader></TableHead>
                    <TableHead className="hidden xl:table-cell"><SortableHeader sort={sort} k="position" testIdPrefix="evals">{t('pages.evaluations.position')}</SortableHeader></TableHead>
                    <TableHead><SortableHeader sort={sort} k="progress" testIdPrefix="evals">{t('pages.evaluations.progress')}</SortableHeader></TableHead>
                    <TableHead className="hidden xl:table-cell"><SortableHeader sort={sort} k="deadline" testIdPrefix="evals">{t('pages.evaluations.deadline')}</SortableHeader></TableHead>
                    <TableHead className="min-w-[210px]"><SortableHeader sort={sort} k="status" testIdPrefix="evals">{t('pages.evaluations.status')}</SortableHeader></TableHead>
                    <TableHead className="w-20 sticky right-0 bg-card z-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && <TableRow><TableCell colSpan={9} className="py-6 text-center text-muted-foreground">{t('common.loading')}</TableCell></TableRow>}
                  {!loading && displayRows.map((e, idx) => {
                    const cName = e.candidate?.name || '—';
                    const showGroupHeader = groupByRequest
                      && (idx === 0 || groupKey(e) !== groupKey(displayRows[idx - 1]));
                    return (
                      <React.Fragment key={e.id}>
                      {showGroupHeader && (
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableCell colSpan={9} className="py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-testid={`evals-group-${groupKey(e) || 'individual'}`}>
                            {groupKey(e)
                              ? `${isES ? 'Solicitud' : 'Request'} ${groupKey(e).slice(0, 8)} · ${displayRows.filter((x) => groupKey(x) === groupKey(e)).length}`
                              : (isES ? 'Evaluaciones individuales' : 'Individual evaluations')}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow
                                className="hover:bg-muted/30 cursor-pointer"
                                onClick={() => navigate(`/admin/evaluations/${e.id}`)}
                                data-testid={`evaluation-row-${e.id}`}>
                        {isHmAdmin && tab !== 'archived' && (
                          <TableCell className="w-8" onClick={(ev) => ev.stopPropagation()}>
                            <span title={isEligible(e) ? undefined
                              : (isES ? 'Solo evaluaciones enviadas, completadas o liberadas pueden archivarse' : 'Only submitted, completed or released evaluations can be archived')}>
                              <Checkbox
                                checked={selected.has(e.id)}
                                disabled={!isEligible(e)}
                                onCheckedChange={() => toggleSelect(e.id)}
                                data-testid={`eval-select-${e.id}`}
                              />
                            </span>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8"><AvatarFallback className="bg-muted text-foreground text-xs font-semibold">{initials(cName)}</AvatarFallback></Avatar>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold">{cName}</p>
                              <p className="text-xs text-muted-foreground max-w-[240px] leading-snug">{localizeAssignmentTitle(e.title, i18n.language)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><span className="text-sm font-medium">{suiteDisplayName(e.suite_code)}</span></TableCell>
                        <TableCell className="hidden lg:table-cell text-sm min-w-[170px]">{e.company?.name || '—'}</TableCell>
                        <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">{e.position || '—'}</TableCell>
                        <TableCell className="w-40">
                          <div className="flex items-center gap-2">
                            <Progress value={e.progress || 0} className="h-1.5 flex-1" />
                            <span className="text-xs font-medium tabular-nums w-9 text-right">{e.progress || 0}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            {e.deadline ? new Date(e.deadline).toLocaleDateString(i18n.language) : '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={e.status} />
                          {assignmentStateReason(e, i18n.language?.startsWith('es')) && (
                            <p className="text-xs text-muted-foreground mt-1 max-w-[220px] leading-snug" data-testid={`eval-row-reason-${e.id}`}>
                              {assignmentStateReason(e, i18n.language?.startsWith('es'))}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="sticky right-0 bg-card">
                          <div className="flex items-center gap-1.5">
                            {['draft', 'requested'].includes(e.status) && (
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-gradient-primary hover:opacity-90"
                                onClick={(ev) => { ev.stopPropagation(); setConfigTarget(e); }}
                                data-testid={`evaluation-configure-${e.id}`}
                              >
                                {e.status === 'draft'
                                  ? (isES ? 'Completar configuración' : 'Finish setup')
                                  : (isES ? 'Revisar configuración' : 'Review setup')}
                              </Button>
                            )}
                            {isHmAdmin && tab !== 'archived' && !e.archived && ['submitted', 'completed', 'released'].includes(e.status) && (
                              <Button
                                variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground"
                                onClick={async (ev) => {
                                  ev.stopPropagation();
                                  try {
                                    await adminApi.archiveAssignment(e.id);
                                    toast.success(isES ? 'Evaluación archivada' : 'Evaluation archived');
                                    reload();
                                  } catch (err) { toast.error(formatApiError(err)); }
                                }}
                                data-testid={`evaluation-archive-${e.id}`}
                              >
                                {isES ? 'Archivar' : 'Archive'}
                              </Button>
                            )}
                            {isHmAdmin && tab !== 'archived' && !e.archived && (
                              <Button
                                variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700"
                                onClick={(ev) => { ev.stopPropagation(); setDeleteEval(e); }}
                                data-testid={`evaluation-delete-${e.id}`}
                                title={isES ? 'Eliminar evaluación' : 'Delete evaluation'}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {isHmAdmin && e.archived && (
                              <Button
                                variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground"
                                onClick={async (ev) => {
                                  ev.stopPropagation();
                                  try {
                                    await adminApi.unarchiveAssignment(e.id);
                                    toast.success(isES ? 'Evaluación restaurada' : 'Evaluation restored');
                                    reload();
                                  } catch (err) { toast.error(formatApiError(err)); }
                                }}
                                data-testid={`evaluation-unarchive-${e.id}`}
                              >
                                {isES ? 'Restaurar' : 'Restore'}
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" data-testid={`evaluation-open-${e.id}`}>{openLabel(e.status)}</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {!loading && !sorted.length && (
                    <TableRow><TableCell colSpan={9} className="py-6 text-center text-muted-foreground italic">{t('pages.evaluations.empty', { defaultValue: 'No evaluations match the filter.' })}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              </SyncedHScroll>
            </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!confirmRows} onOpenChange={(v) => { if (!v) setConfirmRows(null); }}>
        <DialogContent className="max-w-md" data-testid="evals-bulk-confirm">
          <DialogHeader><DialogTitle>{isES ? 'Confirmar archivado' : 'Confirm archiving'}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {(() => { const n = (confirmRows || []).filter(isEligible).length; return isES
              ? `Se archivarán ${n} evaluaciones elegibles. Permanecerán disponibles en la pestaña "Archivadas" y no se eliminará ningún historial.`
              : `${n} eligible evaluations will be archived. They remain available under the "Archived" tab and no history is deleted.`; })()}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRows(null)}>{isES ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={() => doBulkArchive(confirmRows || [])} disabled={bulkArchBusy} data-testid="evals-bulk-confirm-btn">
              {isES ? 'Archivar' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NewEvaluationDialog open={evalOpen} onOpenChange={setEvalOpen} />
      <ConfigureAssignmentDialog
        open={!!configTarget}
        onOpenChange={(v) => !v && setConfigTarget(null)}
        assignment={configTarget}
        onConfigured={reload}
      />
      <EvaluationDeleteDialog evaluation={deleteEval} open={!!deleteEval}
                              onOpenChange={(v) => !v && setDeleteEval(null)} onDone={reload} />
    </>
  );
}
